import React, { useEffect, useState } from 'react';
import {
  Checkbox, DatePicker, Form, Input, Modal, Select, Typography, message,
} from 'antd';
import dayjs from 'dayjs';
import api from '../api';
import ParticipantBulkAdd from './ParticipantBulkAdd';
import LinksFormList, { cleanLinks } from './LinksFormList';

export default function CoachingCreateModal({
  open,
  agentUserId,
  agentName,
  initialTitle,
  initialCampaign,
  initialCampaignId,
  campaignLocked,
  currentUser,
  navigate,
  sourceType,
  sourceId,
  sourceContactId,
  onClose,
  onCreated,
  // A session to copy (the coaching session page's Copy): its fields pre-fill
  // the form.
  copyFrom,
  // The original's first non-system comment, editable here and posted as the
  // copy's first comment.
  copyComment,
}) {
  const [form]        = Form.useForm();
  // Watched so the bulk-add control can append to the current selection.
  const watchedParticipants = Form.useWatch('participant_ids', form);
  const [saving,      setSaving]  = useState(false);
  const [areas,       setAreas]   = useState([]);
  const [coaches,     setCoaches] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [campaigns,   setCampaigns]   = useState([]);
  const [reqTypes,    setReqTypes]    = useState([]);
  // Raised from an evaluation or an error submission: its sections with an
  // occurrence (an error or feedback, on an error), to tick which ones a
  // referral is for.
  const [caseSections, setCaseSections] = useState([]);
  useEffect(() => {
    const src = { case: 'case-sections', error: 'error-sections' }[sourceType];
    if (!open || !src || !sourceId) { setCaseSections([]); return; }
    api.get(`/api/coaching/${src}/${sourceId}`)
      .then((r) => setCaseSections(r.data || []))
      .catch(() => setCaseSections([]));
  }, [open, sourceType, sourceId]);

  const needsParticipantSelect = !agentUserId;

  const loadCoaches = (campaignId, keepCoachId = null) => {
    const params = {};
    if (campaignId) params.campaign_id = campaignId;
    api.get('/api/coaching/coaches', { params }).then((r) => {
      setCoaches(r.data);
      // A copied coach is kept only if still a coach on the campaign.
      if (keepCoachId && !r.data.some((c) => c.ID === keepCoachId)) {
        form.setFieldValue('coach_user_id', null);
      }
    }).catch(() => {});
  };

  // Load static dropdowns once, when first opened (everything except coaches,
  // which depend on campaign) -- the popup is mounted on the eval, error and
  // coaching pages whether or not it is ever used.
  useEffect(() => {
    if (!open) return;
    if (!areas.length)
      api.get('/api/coaching/areas').then((r) => setAreas(r.data)).catch(() => {});
    if (!campaigns.length)
      api.get('/api/coaching/campaigns').then((r) => setCampaigns(r.data)).catch(() => {});
    if (!reqTypes.length)
      api.get('/api/coaching/request-types').then((r) => setReqTypes(r.data)).catch(() => {});
    if (!activeUsers.length)
      api.get('/api/coaching/active-users').then((r) => setActiveUsers(r.data)).catch(() => {});
  }, [open]); // eslint-disable-line

  // Pre-fill on open; load coaches scoped to the campaign when it is known upfront
  useEffect(() => {
    if (!open) return;
    setCoaches([]);
    form.resetFields();
    form.setFieldsValue({
      title:         initialTitle ?? (agentName ? `Coaching Session — ${agentName}` : ''),
      campaign:      initialCampaignId ?? null,
      coach_user_id: currentUser?.CanBeCoach ? (currentUser?.ID ?? null) : null,
      due_date:      dayjs().add(7, 'day'),
      viewer_ids:    currentUser?.ID ? [currentUser.ID] : [],
    });
    if (campaignLocked && initialCampaignId) {
      loadCoaches(initialCampaignId);
    }
    if (copyFrom) {
      form.setFieldsValue({
        title:           copyFrom.Title,
        campaign:        copyFrom.CampaignID ?? null,
        request_type_id: copyFrom.RequestTypeID ?? null,
        area_id:         copyFrom.AreaID ?? null,
        coach_user_id:   copyFrom.CoachUserID ?? null,
        participant_ids: (copyFrom.Participants || []).map((p) => p.UserID),
        viewer_ids:      (copyFrom.Viewers || []).map((v) => v.UserID),
        note:            copyFrom.Note || '',
        urls:            (copyFrom.URLs || []).map((u) => ({ url: u.URL, label: u.Label || '' })),
        first_comment:   copyComment || '',
      });
      if (copyFrom.CampaignID) loadCoaches(copyFrom.CampaignID, copyFrom.CoachUserID);
      else form.setFieldValue('coach_user_id', null);
    }
  }, [open]); // eslint-disable-line

  const onCampaignChange = (campaignId) => {
    form.setFieldValue('coach_user_id', null);
    setCoaches([]);
    if (campaignId) loadCoaches(campaignId);
  };

  const handleCreate = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const interactions = [];
      if (sourceType === 'error' && sourceId) {
        interactions.push({
          error_submission_id: sourceId,
          sections: (values.case_section_ids || []).map((id) => ({ section_id: id })),
        });
      } else if (sourceType === 'case' && sourceId) {
        // Records which evaluation raised the session, and the call alongside
        // it so the interaction still resolves to a recording. Previously an
        // eval-raised session stored only the contact, which made it
        // indistinguishable from one raised off an error on the same call.
        interactions.push({
          case_id: sourceId, contact_id: sourceContactId || null,
          sections: (values.case_section_ids || []).map((id) => ({ section_id: id })),
        });
      } else if (sourceType === 'contact' && sourceId) {
        interactions.push({ contact_id: sourceId });
      }

      const res = await api.post('/api/coaching/sessions', {
        title:           values.title,
        campaign_id:     values.campaign || null,
        request_type_id: values.request_type_id || null,
        area_id:         values.area_id || null,
        due_date:        values.due_date?.format('YYYY-MM-DD') || null,
        coach_user_id:   values.coach_user_id || null,
        participant_ids: agentUserId ? [agentUserId] : (values.participant_ids || []),
        viewer_ids:      values.viewer_ids || [],
        note:            values.note || null,
        interactions,
        urls:            cleanLinks(values.urls),
      });
      const first = copyFrom ? (values.first_comment || '').trim() : '';
      if (first) {
        try {
          await api.post(`/api/coaching/sessions/${res.data.ID}/comments`, { comment: first });
        } catch {
          message.warning('The session was created, but its comment could not be added.');
        }
      }
      message.success('Coaching session created.');
      onClose();
      if (onCreated) {
        onCreated(res.data.ID);
      } else {
        navigate(`/coaching/sessions/${res.data.ID}`);
      }
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to create coaching session.');
    } finally {
      setSaving(false);
    }
  };

  const userCandidates = (() => {
    const vtIds = (currentUser?.VisibleTeams || []).map((t) => t.ID);
    const base = vtIds.length > 0 ? activeUsers.filter((u) => vtIds.includes(u.TeamID)) : activeUsers;
    // Always include currentUser so the pre-filled viewer value resolves before activeUsers loads
    const list = (currentUser?.ID && !base.some((u) => u.ID === currentUser.ID))
      ? [{ ID: currentUser.ID, DisplayName: currentUser.DisplayName || currentUser.Email }, ...base]
      : base;
    return list;
  })();

  const userOptions = userCandidates.map((u) => ({
    label: u.TeamName ? `${u.DisplayName} (${u.TeamName})` : u.DisplayName,
    value: u.ID,
  }));

  const title = agentName ? `New Coaching Session — ${agentName}` : 'New Coaching Session';

  return (
    <Modal maskClosable={false}
      title={title}
      open={open}
      onCancel={onClose}
      onOk={handleCreate}
      confirmLoading={saving}
      okText="Create"
      destroyOnClose
      width={560}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
        <Form.Item label="Title" name="title" rules={[{ required: true, message: 'Required' }]}>
          <Input placeholder="e.g. Communication Skills Coaching" maxLength={255} />
        </Form.Item>

        {campaignLocked ? (
          <>
            <Form.Item label="Campaign">
              <Input value={initialCampaign || ''} disabled />
            </Form.Item>
            <Form.Item name="campaign" hidden><Input /></Form.Item>
          </>
        ) : (
          <Form.Item label="Campaign" name="campaign" rules={[{ required: true, message: 'Required' }]}>
            <Select
              showSearch optionFilterProp="label" placeholder="Select a campaign…"
              options={campaigns.map((c) => ({ label: c.Name, value: c.ID }))}
              onChange={onCampaignChange}
              allowClear
              onClear={() => onCampaignChange(null)}
            />
          </Form.Item>
        )}

        <Form.Item label="Request Type" name="request_type_id" rules={[{ required: true, message: 'Required' }]}>
          <Select
            showSearch optionFilterProp="label" placeholder="Select a request type…"
            options={reqTypes.map((t) => ({ label: t.Name, value: t.ID }))}
          />
        </Form.Item>

        {/* Raised from an evaluation or error with occurrences: which
            sections the session (a referral, typically) is for. Optional. */}
        {caseSections.length > 0 && (
          <Form.Item
            label={(
              <span>
                <Typography.Text strong>Creating a Referral?</Typography.Text>{' '}
                Link which section(s) this is relevant for.
              </span>
            )}
            name="case_section_ids"
          >
            <Checkbox.Group
              style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
              options={caseSections.map((x) => ({ label: x.Name, value: x.ID }))}
            />
          </Form.Item>
        )}

        {/* Optional, and not asked for at all when the session is raised from
            an error -- the error itself already says what it is about. */}
        {sourceType !== 'error' && (
          <Form.Item label="Area" name="area_id">
            <Select
              allowClear
              showSearch optionFilterProp="label" placeholder="Select an area…"
              options={areas.map((a) => ({ label: a.Name, value: a.ID }))}
            />
          </Form.Item>
        )}

        <Form.Item label="Due Date" name="due_date">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item label="Coach" name="coach_user_id" rules={[{ required: true, message: 'Required' }]}>
          <Select
            showSearch optionFilterProp="label"
            placeholder={coaches.length === 0 ? 'Select a campaign first…' : 'Assign a coach…'}
            disabled={coaches.length === 0}
            options={coaches.map((c) => ({ label: c.DisplayName, value: c.ID }))}
          />
        </Form.Item>

        {needsParticipantSelect && (
          <>
            <Form.Item label="Participants" name="participant_ids" rules={[{ required: true, message: 'Required' }]}>
              <Select
                mode="multiple" showSearch optionFilterProp="label" placeholder="Select participants…"
                options={userOptions}
              />
            </Form.Item>
            <ParticipantBulkAdd
              users={userCandidates}
              value={watchedParticipants || []}
              onChange={(ids) => form.setFieldValue('participant_ids', ids)}
              noun="participants"
            />
          </>
        )}

        <Form.Item label="Viewers" name="viewer_ids">
          <Select
            mode="multiple" showSearch optionFilterProp="label" allowClear placeholder="Select viewers (optional)…"
            options={userOptions}
          />
        </Form.Item>

        <Form.Item label="Note" name="note">
          <Input.TextArea rows={3} placeholder="Optional notes…" maxLength={4000} showCount />
        </Form.Item>

        <Form.Item label="Links">
          <LinksFormList name="urls" />
        </Form.Item>

        {copyFrom && (
          <Form.Item label="Comment" name="first_comment">
            <Input.TextArea autoSize={{ minRows: 3, maxRows: 12 }} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
